/**
 * GenAI Prices Source - pydantic/genai-prices
 *
 * Fetches model pricing from GitHub-hosted JSON (CORS-friendly)
 * @see https://github.com/pydantic/genai-prices
 */
/**
 * Provider ID mapping from genai-prices to our provider names
 */
/**
 * Provider ID mapping from genai-prices to our provider names.
 * Only includes providers we actually use in the app.
 */
const PROVIDER_ID_MAP = {
    anthropic: 'anthropic',
    openai: 'openai',
    'google-vertex': 'google',
    'google-ai-studio': 'google',
    groq: 'groq',
    deepinfra: 'deepinfra',
};
/**
 * Create lookup keys for a model
 */
const createLookupKeys = (providerId, model) => {
    const keys = [];
    // Add the model ID directly
    keys.push(model.id);
    // Add provider-prefixed version
    const ourProvider = PROVIDER_ID_MAP[providerId];
    if (ourProvider) {
        keys.push(`${ourProvider}:${model.id}`);
    }
    // Extract match patterns as additional keys
    if (model.match) {
        if (model.match.equals) {
            keys.push(model.match.equals);
        }
        if (model.match.starts_with) {
            keys.push(model.match.starts_with);
        }
        if (model.match.or) {
            for (const pattern of model.match.or) {
                if (pattern.equals)
                    keys.push(pattern.equals);
                if (pattern.starts_with)
                    keys.push(pattern.starts_with);
            }
        }
    }
    return [...new Set(keys)];
};
/**
 * Fetch pricing data from pydantic/genai-prices
 */
export const fetchGenAIPricing = async () => {
    const response = await fetch('https://raw.githubusercontent.com/pydantic/genai-prices/main/prices/data_slim.json', {
        headers: {
            Accept: 'application/json',
        },
    });
    if (!response.ok) {
        throw new Error(`genai-prices fetch returned ${response.status}: ${response.statusText}`);
    }
    const providers = await response.json();
    const models = {};
    for (const provider of providers) {
        // Skip providers we don't support (only process those in PROVIDER_ID_MAP)
        if (!PROVIDER_ID_MAP[provider.id]) {
            continue;
        }
        for (const model of provider.models) {
            if (model.prices?.input_mtok !== undefined && model.prices?.output_mtok !== undefined) {
                const pricing = {
                    inputPer1M: model.prices.input_mtok,
                    outputPer1M: model.prices.output_mtok,
                };
                // Add cached pricing if available
                if (model.prices.cache_read_mtok !== undefined) {
                    pricing.cachedInputPer1M = model.prices.cache_read_mtok;
                }
                // Add all lookup keys for this model
                const keys = createLookupKeys(provider.id, model);
                for (const key of keys) {
                    models[key] = pricing;
                }
            }
        }
    }
    console.log(`[GenAIPrices] Fetched pricing for ${Object.keys(models).length} model variants`);
    return {
        models,
        fetchedAt: Date.now(),
    };
};
